import React, { useEffect, useRef, useState } from 'react';
import ReactRailsUJS from '@/modules/ReactRailsUJS';
import HorizontalScrollMenu, { MenuItem } from '@/components/common/HorizontalScrollMenu';

interface SectionTabsProps {
  items: { title: string; html: string; }[];
  selected?: string;
}

// AIDEV-NOTE: PLAT-17431 — this is a button group, NOT a role="tablist". The scroll library wraps
// every child in its own <div>, so role="tab" children would never be owned by a tablist parent.
// Don't "fix" the roles without replacing react-horizontal-scrolling-menu-7 first.
//
// AIDEV-NOTE: PLAT-17624 — arrow keys move focus but do NOT select. Automatic activation is the
// tablist pattern, and it would be wrong here: each panel holds a live Froala editor, so arrowing
// past a tab would mount and tear down editors the author never asked for.
const FOCUS_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

const focusedTabIndex = (key: string, current: number, count: number) => {
  switch (key) {
    case 'ArrowLeft': return (current - 1 + count) % count;
    case 'ArrowRight': return (current + 1) % count;
    case 'Home': return 0;
    default: return count - 1;
  }
};

// React 16 has no useId, and two tab strips can share a page (the reference material builder mounts
// one per reading), so panel ids have to be unique per instance rather than derived from the title.
let instanceCount = 0;
const nextPanelIdPrefix = () => {
  instanceCount += 1;

  return `section-tabs-${instanceCount}`;
};

const SectionTabs = ({ items, selected: initialSelected }: SectionTabsProps) => {
  const [selected, setSelected] = useState<string>(initialSelected || items[0].title);
  const isItemSelected = (title: string): boolean => title === selected;
  const selectedPanelRef = useRef<HTMLDivElement>(null);
  const tabStripRef = useRef<HTMLDivElement>(null);
  const shouldFocusPanel = useRef(false);
  const [panelIdPrefix] = useState(nextPanelIdPrefix);

  const panelId = (index: number) => `${panelIdPrefix}-panel-${index}`;

  // AIDEV-NOTE: PLAT-17431 moves focus into the panel on selection so screen-reader users are taken
  // to the new content instead of it changing silently. PLAT-17624 narrowed that to keyboard
  // activation: `detail === 0` marks a click synthesized from Enter/Space, and stealing focus from a
  // real pointer click stranded mouse users — the arrow keys below are bound to the tabs, so the
  // strip went dead the moment you clicked one. A keyboard user's next step is Tab into the content
  // anyway, and Shift+Tab brings them back to the strip.
  const selectTab = (title: string, event: React.MouseEvent<HTMLButtonElement>) => {
    shouldFocusPanel.current = event.detail === 0;
    setSelected(title);
  };

  useEffect(() => {
    document.querySelectorAll('.CodeMirror').forEach((el) => {
      (el as HTMLElement & { CodeMirror?: { refresh: () => void } }).CodeMirror?.refresh();
    });
    ReactRailsUJS.mountComponents();

    if (shouldFocusPanel.current) selectedPanelRef.current?.focus();
    shouldFocusPanel.current = false;
  }, [selected]);

  // AIDEV-NOTE: PLAT-17624 — `button[aria-controls]` is the selector because the scroll library puts
  // its own arrow <button>s in this subtree; only the tabs point at a panel. The handler hangs off
  // each tab rather than the group wrapper so it sits on a natively interactive element.
  const moveTabFocus = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!FOCUS_KEYS.includes(event.key)) return;

    const tabs = Array.from(
      tabStripRef.current?.querySelectorAll<HTMLButtonElement>('button[aria-controls]') || [],
    );
    const current = tabs.indexOf(event.currentTarget);

    if (current === -1) return;

    event.preventDefault();
    tabs[focusedTabIndex(event.key, current, tabs.length)].focus();
  };

  return (
    <>
      <div ref={tabStripRef} aria-label="Content tabs" role="group">
        <HorizontalScrollMenu hideArrows>
          {items.map(({ title }, index) => (
            <MenuItem
              key={title}
              ariaControlId={panelId(index)}
              selected={isItemSelected(title)}
              text={title}
              onClick={selectTab}
              onKeyDown={moveTabFocus}
            />
          ))}
        </HorizontalScrollMenu>
      </div>
      <div className="tw-border-solid tw-border-t tw-border-[#ddd] tw-pt-4 tw-relative">
        {items.map(({ title, html }, index) => (
          <div
            key={title}
            ref={isItemSelected(title) ? selectedPanelRef : undefined}
            aria-label={title}
            className={isItemSelected(title) ? '' : 'tw-hidden'}
            dangerouslySetInnerHTML={{ __html: html }}
            id={panelId(index)}
            role="region"
            tabIndex={-1}
          />
        ))}
      </div>
    </>
  );
};

export default SectionTabs;
